import { useQuery } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { convertFromSEK } from 'src/shared/utils/currency'
import { buildSequentialIlikePattern } from 'src/shared/utils/searchMatch'
import { Database } from 'supabase/database.types'
import { queryKeys } from './queryKeys'

export type InsertProduct = Database['public']['Tables']['product']['Insert']
export type Product_ = Database['public']['Tables']['product']['Row'] & {
  product_price: number
  product_variant?: (Database['public']['Tables']['product_variant']['Row'] & {
    product_variant_color?: (Database['public']['Tables']['product_variant_color']['Row'] & {
      product_image?: Database['public']['Tables']['product_image']['Row'][]
    })[]
  })[]
}
// btw jag saknar varianter i db

export const useProductsOpenApi = ({
  article_numbers,
  product_id,
  product_ids,
  page = 1,
  pageSize = 25,
  searchArticleNumber,
  searchItemName,
  searchSource,
  searchText,
  klayder_recommends,
}: {
  article_numbers?: string[]
  product_id?: number
  product_ids?: number[]
  page?: number
  pageSize?: number
  searchArticleNumber?: string
  searchItemName?: string
  searchSource?: string
  searchText?: string
  klayder_recommends?: boolean
}) => {
  const supabase = useSupabaseClient()
  const currency = useOrganizationCurrency()

  const { data: productsByIdsOpen, isPending: isLoadingProducts2 } = useQuery({
    queryKey: [
      queryKeys.products,
      page,
      pageSize,
      currency,
      searchArticleNumber,
      searchItemName,
      searchSource,
      searchText,
      product_id,
      product_ids,
      klayder_recommends,
      article_numbers,
    ].filter(Boolean),

    queryFn: async () => {
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // Use product_open view instead of product table to respect RLS
      // The view excludes sensitive fields (cost, margin, source)
      // @ts-ignore - TODO1
      let query = supabase.from('product_open').select(
        `id,
          article_number,
          is_draft,
          fast_track,
          has_variants,
          moq,
          item_name,
          item_name_translations,
          item_description,
          item_description_translations,
          print_techniques,
          packaging_cost,
          price,
          created_at,
          klayder_recommends,
          klayder_recommendation_text,
          related_product_ids,
          
          category_product!inner(*, category!inner(*)),

          product_variant(*, 
            product_variant_color(*, 
              product_image(*)))`,
        { count: 'exact' }
      )

      if (searchText) {
        const seq = buildSequentialIlikePattern(searchText)
        query = query.or(
          `article_number.ilike.%${searchText}%, item_name_translations.ilike.%${searchText}%, item_description_translations.ilike.%${searchText}%, item_name_translations.ilike.${seq}, item_description_translations.ilike.${seq}`
        )
      } else if (searchArticleNumber) {
        query = query.ilike('article_number', `%${searchArticleNumber}%`)
      } else if (searchItemName) {
        const seq = buildSequentialIlikePattern(searchItemName)
        query = query.or(
          `item_name_translations.ilike.%${searchItemName}%, item_description_translations.ilike.%${searchItemName}%, item_name_translations.ilike.${seq}, item_description_translations.ilike.${seq}`
        )
      } else if (product_id) {
        query = query.eq('id', product_id)
      } else if (product_ids) {
        query = query.in('id', product_ids)
      }
      if (klayder_recommends) {
        query = query.eq('klayder_recommends', true)
      }
      if (article_numbers) {
        query = query.in(
          'article_number',
          article_numbers.map((article_number) => article_number.trim())
        )
      }

      // console.log('query', query)

      const { data, count, error } = await query
        .range(from, to)
        .order('created_at', { ascending: false, nullsFirst: false })
        .or('is_draft.is.null,is_draft.eq.false')

      // console.log('data', data)
      // console.log('count', count)
      // console.log('error', error)

      if (error) throw error

      const dataWithConvertedPrices = (data ?? []).map((product) => ({
        ...product,
        price: convertFromSEK(product.price, currency) ?? product.price,
        packaging_cost:
          convertFromSEK(product.packaging_cost, currency) ??
          product.packaging_cost,
      }))

      return {
        data: dataWithConvertedPrices,
        count,
        page,
        pageSize,
        totalPages: count ? Math.ceil(count / pageSize) : 0,
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled:
      (!!product_ids && product_ids.length > 0) ||
      !!product_id ||
      !!searchText ||
      !!searchArticleNumber ||
      !!searchItemName ||
      !!searchSource ||
      !!klayder_recommends ||
      (!!article_numbers && article_numbers?.length > 0),
  })

  return {
    productsByIdsOpen,
    isLoadingProducts2,
  }
}
